import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import * as THREE from "three";
import { prefersReducedMotion } from "@/lib/anime/prefersReducedMotion";
import { readHeroPerf } from "@/lib/anime/useHeroPerf";

/**
 * HeroGlobeStage · single canvas for the hero + warp phases.
 *
 * One freight globe, two camera phases. Phase A is the hero orbit.
 * Phase B is the warp dolly that flies the camera straight INTO the
 * same globe. The user perceives one continuous spin, the camera
 * just gets closer — no second globe spawns.
 *
 * Mounts as a sticky-pinned canvas inside the hero+warp stage
 * container so it stays on screen across both sections, then
 * releases when the warp ends.
 */
const GLOBE_RADIUS = 1.7;
const STAR_COUNT = 220;

const HUBS = [
  { name: "vancouver", lat: 49.3, lon: -123.1 },
  { name: "los_angeles", lat: 34.0, lon: -118.2 },
  { name: "houston", lat: 29.8, lon: -95.4 },
  { name: "miami", lat: 25.8, lon: -80.2 },
  { name: "atlanta", lat: 33.7, lon: -84.4 },
  { name: "nyc", lat: 40.7, lon: -74.0 },
  { name: "toronto", lat: 43.7, lon: -79.4 },
  { name: "chicago", lat: 41.9, lon: -87.6 },
  { name: "montreal", lat: 45.5, lon: -73.6 },
  { name: "calgary", lat: 51.0, lon: -114.1 },
  { name: "dallas", lat: 32.8, lon: -96.8 },
  { name: "denver", lat: 39.7, lon: -105.0 },
] as const;

const ROUTES: Array<[number, number]> = [
  [1, 5],
  [0, 7],
  [2, 3],
  [6, 4],
  [8, 5],
  [9, 11],
  [10, 3],
];

function readPrimaryHsl(): string {
  if (typeof window === "undefined") return "#3DDC97";
  const v = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  return v ? `hsl(${v})` : "#3DDC97";
}

function latLonToVec(lat: number, lon: number, r: number): THREE.Vector3 {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
}

function buildArc(a: THREE.Vector3, b: THREE.Vector3, segments = 64): THREE.Vector3[] {
  const mid = a.clone().add(b).multiplyScalar(0.5).normalize().multiplyScalar(GLOBE_RADIUS * 1.32);
  return new THREE.QuadraticBezierCurve3(a, mid, b).getPoints(segments);
}

const ORBIT_RADIUS = 6.2;
const ORBIT_HEIGHT = 0.4;
const ORBIT_SPEED = 0.05;
const ORBIT_PHASE = Math.PI / 2;

function UnifiedCamera({ scrollRef, reduced }: { scrollRef: React.MutableRefObject<number>; reduced: boolean }) {
  const { camera } = useThree();
  const offset = useRef({ x: 0, y: 0 });
  const easedHero = useRef(0);

  useFrame((state, dt) => {
    if (reduced) {
      camera.position.set(0, ORBIT_HEIGHT, ORBIT_RADIUS);
      camera.lookAt(0, 0, 0);
      return;
    }
    const s = scrollRef.current; // 0..2 across hero+warp
    if (s <= 1) {
      // Phase A · hero orbit. Same as the original OrbitingCamera with
      // pointer offset and gentle scroll-tilt.
      const targetX = state.pointer.x * 0.6;
      const targetY = state.pointer.y * 0.35;
      offset.current.x += (targetX - offset.current.x) * Math.min(1, dt * 2.4);
      offset.current.y += (targetY - offset.current.y) * Math.min(1, dt * 2.4);

      easedHero.current += (s - easedHero.current) * Math.min(1, dt * 3.5);
      const sH = easedHero.current;
      const dynamicSpeed = ORBIT_SPEED + sH * 0.18;
      const dynamicRadius = ORBIT_RADIUS + sH * 0.6;
      const tiltUp = sH * 0.6;

      const t = state.clock.elapsedTime * dynamicSpeed + ORBIT_PHASE;
      const orbitX = Math.cos(t) * dynamicRadius + offset.current.x;
      const orbitZ = Math.sin(t) * dynamicRadius;
      const orbitY = ORBIT_HEIGHT + tiltUp + Math.sin(t * 0.5) * 0.25 + offset.current.y;

      camera.position.set(orbitX, orbitY, orbitZ);
      camera.lookAt(0, 0, 0);
    } else {
      // Phase B · spiral-dive INTO the globe. Camera keeps orbiting
      // while the radius collapses, so the user feels like they're
      // rotating AROUND the planet as they fall toward it. Two stages:
      //   Approach (0..70%)  radius 6.8 → 2.0 (still outside surface)
      //   Puncture (70..100%) radius 2.0 → 0.4 (inside the wireframe)
      // Globe stays at scale 1; visual growth comes from camera distance.
      const w = Math.min(1, s - 1); // 0..1 within warp
      const wEased = 1 - Math.pow(1 - w, 3); // cubic-out

      // Approach pulls the camera from 6.8 to 1.6 (just outside the
      // wireframe at radius 1.7) over the first 65% of the warp so the
      // globe visibly grows to fill the viewport before puncture.
      const aRaw = Math.min(1, w / 0.65);
      const aEased = 1 - Math.pow(1 - aRaw, 3);
      const approachRadius = THREE.MathUtils.lerp(ORBIT_RADIUS + 0.6, 1.6, aEased);

      // Puncture takes the camera from 1.6 to 0.2 · we cross the surface
      // and end up near the center, looking out through the back wall.
      const pRaw = Math.max(0, Math.min(1, (w - 0.65) / 0.35));
      const pEased = 1 - Math.pow(1 - pRaw, 3);
      const radius = w < 0.65
        ? approachRadius
        : THREE.MathUtils.lerp(1.6, 0.2, pEased);

      // Angular speed accelerates as radius shrinks · stronger g-force
      // sense without the camera ever stopping its rotation.
      const accel = 1 + wEased * 1.8;
      const t = state.clock.elapsedTime * (ORBIT_SPEED + 0.18) * accel + ORBIT_PHASE;

      // Slight vertical bob that flattens out as we punch through.
      const tilt = Math.sin(state.clock.elapsedTime * 0.6) * 0.15 * (1 - wEased);

      const orbitX = Math.cos(t) * radius;
      const orbitZ = Math.sin(t) * radius;
      const orbitY = THREE.MathUtils.lerp(ORBIT_HEIGHT + 0.6, 0.0, wEased) + tilt;
      camera.position.set(orbitX, orbitY, orbitZ);
      camera.lookAt(0, 0, 0);
    }
  });
  return null;
}

function ArcPulse({
  points,
  color,
  offset = 0,
  speed = 0.18,
  reduced,
}: {
  points: THREE.Vector3[];
  color: string;
  offset?: number;
  speed?: number;
  reduced: boolean;
}) {
  const ref = useRef<THREE.Group>(null);
  const matRef = useRef<THREE.MeshBasicMaterial>(null);
  useFrame((state) => {
    if (reduced || !ref.current) return;
    const raw = state.clock.elapsedTime * speed + offset;
    const t = raw - Math.floor(raw);
    const last = points.length - 1;
    const idx = Math.min(last, Math.max(0, Math.floor(t * last)));
    const p = points[idx];
    ref.current.position.copy(p);
    const pulse = Math.sin(t * Math.PI);
    const scale = 0.5 + pulse * 0.6;
    ref.current.scale.setScalar(scale);
    if (matRef.current) matRef.current.opacity = 0.35 + pulse * 0.6;
  });
  return (
    <group ref={ref}>
      <mesh>
        <sphereGeometry args={[0.07, 14, 14]} />
        <meshBasicMaterial ref={matRef} color={color} transparent opacity={0.9} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
}

function FreightGlobe({
  scrollRef,
  color,
  reduced,
}: {
  scrollRef: React.MutableRefObject<number>;
  color: string;
  reduced: boolean;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const wireMatRef = useRef<THREE.MeshBasicMaterial>(null);
  const innerMatRef = useRef<THREE.MeshBasicMaterial>(null);
  const atmoMatRef = useRef<THREE.MeshBasicMaterial>(null);

  const hubPositions = useMemo(
    () => HUBS.map((h) => latLonToVec(h.lat, h.lon, GLOBE_RADIUS * 1.005)),
    [],
  );
  const arcs = useMemo(
    () =>
      ROUTES.map(([i, j]) =>
        buildArc(
          latLonToVec(HUBS[i].lat, HUBS[i].lon, GLOBE_RADIUS),
          latLonToVec(HUBS[j].lat, HUBS[j].lon, GLOBE_RADIUS),
        ),
      ),
    [],
  );

  // Entry "launch" · advances 0→1 over ~1.4s when the canvas mounts so
  // the globe scales up + brightens instead of appearing fully formed.
  // Reduced-motion users skip the entry and start at full state.
  const entryRef = useRef(reduced ? 1 : 0);
  const ENTRY_DURATION = 1.4;

  useFrame((_, dt) => {
    if (!groupRef.current) return;
    const s = scrollRef.current;
    // Advance entry progress.
    if (entryRef.current < 1) {
      entryRef.current = Math.min(1, entryRef.current + dt / ENTRY_DURATION);
    }
    const eRaw = entryRef.current;
    // easeOutCubic for a smooth settle.
    const e = 1 - Math.pow(1 - eRaw, 3);
    // Hero phase · slow continuous spin. Warp phase · accelerate.
    // Entry phase · spin extra fast for the first second so it reads as a launch.
    const baseSpin = 0.08;
    const entrySpin = (1 - e) * 0.45;
    const warpSpin = s > 1 ? Math.min(1, s - 1) * 0.6 : 0;
    if (!reduced) groupRef.current.rotation.y += dt * (baseSpin + entrySpin + warpSpin);
    if (s > 1) {
      const w = Math.min(1, s - 1);
      // Globe stays scale 1 most of the warp · the camera radius collapse
      // does the visual growth. Just before puncture we bloom slightly so
      // the wireframe overruns the camera and the user feels engulfed.
      const scale = 1 + Math.max(0, w - 0.8) * 5;
      groupRef.current.scale.setScalar(scale);
      // Wireframe stays bold for most of the dive · only fades in the
      // last sliver so the user actually SEES the cage grow huge before
      // the FlashCore takes over.
      const wireFade = w > 0.88 ? Math.max(0, 1 - (w - 0.88) * 8.3) : 1;
      // Inner shell vanishes immediately at warp start · it exists in the
      // hero phase to give the wireframe contrast against the backdrop,
      // but during the dive it reads as an opaque dark circle floating
      // inside the cage. Kill it fast so the globe looks like a true
      // transparent wireframe we're flying into.
      const innerFade = Math.max(0, 1 - w * 6);
      if (wireMatRef.current) wireMatRef.current.opacity = 0.82 * wireFade;
      if (innerMatRef.current) innerMatRef.current.opacity = 0.78 * innerFade;
      if (atmoMatRef.current) atmoMatRef.current.opacity = 0.10 * wireFade;
    } else {
      // Hero phase · scale 0.55→1 + opacity climbs during entry, then settles.
      const entryScale = 0.55 + e * 0.45;
      groupRef.current.scale.setScalar(entryScale);
      if (wireMatRef.current) wireMatRef.current.opacity = 0.82 * e;
      if (innerMatRef.current) innerMatRef.current.opacity = 0.78 * e;
      if (atmoMatRef.current) atmoMatRef.current.opacity = 0.10 * e;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh scale={0.985}>
        <sphereGeometry args={[GLOBE_RADIUS, 48, 32]} />
        <meshBasicMaterial ref={innerMatRef} color="#000000" transparent opacity={0.55} />
      </mesh>
      <mesh>
        <sphereGeometry args={[GLOBE_RADIUS, 48, 32]} />
        <meshBasicMaterial ref={wireMatRef} color={color} transparent opacity={0.55} wireframe />
      </mesh>
      <mesh scale={1.08}>
        <sphereGeometry args={[GLOBE_RADIUS, 32, 24]} />
        <meshBasicMaterial ref={atmoMatRef} color={color} transparent opacity={0.06} />
      </mesh>
      {hubPositions.map((p, i) => (
        <group key={i} position={[p.x, p.y, p.z]}>
          <mesh>
            <sphereGeometry args={[0.13, 14, 14]} />
            <meshBasicMaterial color={color} transparent opacity={0.22} blending={THREE.AdditiveBlending} />
          </mesh>
          <mesh>
            <sphereGeometry args={[0.085, 14, 14]} />
            <meshBasicMaterial color={color} transparent opacity={0.55} blending={THREE.AdditiveBlending} />
          </mesh>
          <mesh>
            <sphereGeometry args={[0.05, 14, 14]} />
            <meshBasicMaterial color={color} />
          </mesh>
        </group>
      ))}
      {arcs.map((points, i) => (
        <Line key={i} points={points} color={color} lineWidth={1.5} transparent opacity={0.7} />
      ))}
      {!reduced &&
        arcs.map((points, i) => (
          <ArcPulse
            key={`pulse-${i}`}
            points={points}
            color={color}
            offset={i * 0.18}
            speed={0.16 + (i % 3) * 0.04}
            reduced={reduced}
          />
        ))}
    </group>
  );
}

function StarField({ scrollRef, color }: { scrollRef: React.MutableRefObject<number>; color: string }) {
  const ref = useRef<THREE.Points>(null);
  const matRef = useRef<THREE.PointsMaterial>(null);
  const positions = useMemo(() => {
    const arr = new Float32Array(STAR_COUNT * 3);
    for (let i = 0; i < STAR_COUNT; i++) {
      arr[i * 3 + 0] = (Math.random() - 0.5) * 22;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 2] = -25 + Math.random() * 30;
    }
    return arr;
  }, []);

  useFrame((_, dt) => {
    if (!ref.current) return;
    const s = scrollRef.current;
    // Stars only visible during warp phase.
    const w = s > 1 ? Math.min(1, s - 1) : 0;
    if (matRef.current) matRef.current.opacity = w * 0.85;
    const attr = ref.current.geometry.attributes.position as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    const speed = w * 18 + 1.2;
    for (let i = 0; i < STAR_COUNT; i++) {
      const zi = i * 3 + 2;
      arr[zi] += dt * speed;
      if (arr[zi] > 6) {
        arr[zi] = -28;
        arr[i * 3 + 0] = (Math.random() - 0.5) * 22;
        arr[i * 3 + 1] = (Math.random() - 0.5) * 14;
      }
    }
    attr.needsUpdate = true;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial ref={matRef} color={color} size={0.045} sizeAttenuation transparent opacity={0} />
    </points>
  );
}

function FlashCore({ scrollRef, color }: { scrollRef: React.MutableRefObject<number>; color: string }) {
  const ref = useRef<THREE.Mesh>(null);
  const matRef = useRef<THREE.MeshBasicMaterial>(null);
  useFrame(() => {
    if (!ref.current) return;
    const s = scrollRef.current;
    // Only ignites at the very end of warp phase.
    const w = s > 1 ? s - 1 : 0;
    const intensity = Math.max(0, (w - 0.6) * 2.4);
    ref.current.scale.setScalar(0.1 + intensity * 1.6);
    if (matRef.current) matRef.current.opacity = Math.min(0.95, intensity * 0.9);
  });
  return (
    <mesh ref={ref}>
      <sphereGeometry args={[1, 24, 24]} />
      <meshBasicMaterial
        ref={matRef}
        color={color}
        transparent
        opacity={0}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
}

export function HeroGlobeStage({ stageId }: { stageId: string }) {
  const reduced = useMemo(() => prefersReducedMotion(), []);
  const color = useMemo(() => readPrimaryHsl(), []);
  const maxDpr = useMemo(() => readHeroPerf().maxDpr, []);
  const wrapRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef(0);
  const [active, setActive] = useState(true);
  const [exitOpacity, setExitOpacity] = useState(1);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) setActive(false);
    };
    document.addEventListener("visibilitychange", onVis);
    const el = wrapRef.current;
    let io: IntersectionObserver | null = null;
    if (el && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        ([entry]) => setActive(entry.isIntersecting && !document.hidden),
        { threshold: 0.05 },
      );
      io.observe(el);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      io?.disconnect();
    };
  }, []);

  // Compute scroll progress over the combined hero+warp stage.
  // 0..1 = hero in view, 1..2 = warp in view.
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const stage = document.getElementById(stageId);
      const heroEl = document.querySelector<HTMLElement>(`#${stageId} > section:first-of-type`);
      if (!stage || !heroEl) return;
      const stageRect = stage.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const h = window.innerHeight;
      // Hero progress · 0 when hero top at viewport top, 1 when hero bottom at viewport top.
      const heroH = Math.max(1, heroRect.height);
      const heroProgress = Math.max(0, Math.min(1, -heroRect.top / heroH));
      // Warp progress · only after hero ends. The warp section is the second child.
      const warpH = Math.max(1, stageRect.height - heroRect.height - h);
      const warpPassed = -stageRect.top - heroRect.height;
      const warpProgress = Math.max(0, Math.min(1, warpPassed / warpH));
      const combined = heroProgress + warpProgress;
      scrollRef.current = combined;
      // Exit fade · canvas dims at end of warp so the next section reveals.
      const w = combined > 1 ? combined - 1 : 0;
      setExitOpacity(w > 0.92 ? Math.max(0, 1 - (w - 0.92) * 12) : 1);
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [stageId]);

  const frameloop: "always" | "demand" | "never" = reduced
    ? "demand"
    : active
    ? "always"
    : "never";

  return (
    <div
      ref={wrapRef}
      className="hidden md:block absolute inset-0 pointer-events-none z-0"
      aria-hidden="true"
      style={{ opacity: exitOpacity, transition: "opacity 0.2s linear" }}
    >
      <div className="sticky top-0 w-full h-svh">
        <Canvas
          camera={{ position: [0, ORBIT_HEIGHT, ORBIT_RADIUS], fov: 45 }}
          dpr={[1, maxDpr]}
          gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
          eventSource={typeof document !== "undefined" ? document.documentElement : undefined}
          eventPrefix="client"
          frameloop={frameloop}
        >
          <ambientLight intensity={0.6} />
          <Suspense fallback={null}>
            <UnifiedCamera scrollRef={scrollRef} reduced={reduced} />
            <FreightGlobe scrollRef={scrollRef} color={color} reduced={reduced} />
            <StarField scrollRef={scrollRef} color={color} />
            <FlashCore scrollRef={scrollRef} color={color} />
          </Suspense>
        </Canvas>
      </div>
    </div>
  );
}

export default HeroGlobeStage;
